import { formatSSN } from "@/shared/utils/formatters";

import type {
  FormField,
  FormFieldOption,
  FormSectionConfig,
} from "@/shared/types/estateEssentials";

export function groupFieldsByRow(fields: FormField[]): FormField[][] {
  const groups: FormField[][] = [];
  let currentGroup: FormField[] = [];
  let currentRow: string | undefined;

  for (const field of fields) {
    if (field.row && field.row === currentRow) {
      currentGroup.push(field);
    } else {
      if (currentGroup.length > 0) groups.push(currentGroup);
      currentGroup = [field];
      currentRow = field.row;
    }
  }
  if (currentGroup.length > 0) groups.push(currentGroup);

  return groups;
}

export function getGridClass(count: number): string {
  if (count === 3) return "grid grid-cols-1 sm:grid-cols-3 gap-3";
  if (count === 2) return "grid grid-cols-1 sm:grid-cols-2 gap-3";
  return "";
}

export function resolveFieldLabel(field: FormField, deceasedFirstName?: string): string {
  if (field.label.includes("{{ firstName }}") && deceasedFirstName) {
    return field.label.replace(/\{\{ firstName \}\}/g, deceasedFirstName);
  }
  if (field.key === "user_relationship" && deceasedFirstName) {
    return `I am ${deceasedFirstName}'s...`;
  }
  return field.label;
}

export function resolveTemplate(text: string, deceasedFirstName?: string): string {
  if (text.includes("{{ firstName }}") && deceasedFirstName) {
    return text.replace(/\{\{ firstName \}\}/g, deceasedFirstName);
  }
  return text;
}

export function getOptionValue(opt: FormFieldOption): string {
  return typeof opt === "string" ? opt : opt.value;
}

export function getOptionLabel(opt: FormFieldOption): string {
  return typeof opt === "string" ? opt : opt.label;
}

export function getDisplayValue(field: FormField, value: string): string {
  if (!value) return "-";

  if (field.type === "ssn") {
    return "XXX-XX-XXXX";
  }

  if (field.type === "date") {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, , month, day] = match;
      return `${month}/${day}/${match[1]}`;
    }
  }

  if (field.type === "select" && field.options) {
    const match = field.options.find((opt: FormFieldOption) => getOptionValue(opt) === value);
    if (match) {
      return getOptionLabel(match);
    }
  }

  return value;
}

export function getRevealedDisplayValue(field: FormField, value: string): string {
  if (!value) return "-";

  if (field.type === "ssn") {
    return formatSSN(value);
  }

  return getDisplayValue(field, value);
}

export function splitFieldsBySection(fields: FormField[]): {
  reviewFields: FormField[];
  editableFields: FormField[];
} {
  const reviewFields: FormField[] = [];
  const editableFields: FormField[] = [];

  for (const field of fields) {
    if (field.section === "review") {
      reviewFields.push(field);
    } else {
      editableFields.push(field);
    }
  }

  return { reviewFields, editableFields };
}

export function groupFieldsBySectionGroup(
  fields: FormField[],
  sections: FormSectionConfig[],
): Map<string, FormField[]> {
  const map = new Map<string, FormField[]>();
  for (const s of sections) {
    map.set(s.key, []);
  }
  for (const f of fields) {
    const key =
      f.sectionGroup ??
      (f.section === "review" ? sections[0]?.key : sections[sections.length - 1]?.key) ??
      "";
    const list = map.get(key);
    if (list) list.push(f);
  }
  return map;
}

export function extractAddressFieldInfo(fields: FormField[]) {
  const addressField = fields.find((f) => f.type === "address");
  const addrPrefix = addressField ? addressField.key.replace(/_address$/, "") : "";
  const addressRelatedKeys = addrPrefix
    ? new Set([
        `${addrPrefix}_address`,
        `${addrPrefix}_apartment`,
        `${addrPrefix}_city`,
        `${addrPrefix}_state`,
        `${addrPrefix}_zip`,
      ])
    : new Set<string>();
  const nonAddressFields = fields.filter((f) => !addressRelatedKeys.has(f.key));
  return { addressField, addrPrefix, addressRelatedKeys, nonAddressFields };
}
